/**
 * useAnswerLane.ts — React-Query orchestrator for the Answer Lane page
 *
 * W21: Primary Answer Surface — /accounts/:id/answer
 *
 * Loads questions, CP progress, account header.
 * Exposes save (debounced autosave + explicit save), advance logic.
 *
 * Endpoints used (all existing):
 *   GET  /api/accounts/:id             — account header
 *   GET  /api/accounts/:id/cp-progress — per-CP state
 *   GET  /api/accounts/:id/questions   — full question list with answer state
 *   PUT  /api/accounts/:id/answers/:questionKey — save answer
 */

import { useCallback, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/session";
import { CP_WEIGHTS } from "@shared/scoring/cp-weights";

// ── Canon CP names (locked) ────────────────────────────────────────────────
export const CP_NAMES: Record<number, string> = {
  1:  "CP1 — Project (5%)",
  2:  "CP2 — Need / Pain / Demonstration (20%)",
  3:  "CP3 — Budget (10%)",
  4:  "CP4 — Timeline (10%)",
  5:  "CP5 — Authority — Project / Initiative (15%)",
  6:  "CP6 — Technical and Financial Sign-off (15%)",
  7:  "CP7 — Org Structure and Influence Map (15%)",
  8:  "CP8 — Competitive Position (5%)",
  9:  "CP9 — Compliance — IT Internal and Industry-Specific (3%)",
  10: "CP10 — Legal — Contracts, SLAs, Terms (2%)",
};

// Short label for CP picker
export const CP_SHORT_NAMES: Record<number, string> = {
  1:  "CP1 — Project",
  2:  "CP2 — Need / Pain",
  3:  "CP3 — Budget",
  4:  "CP4 — Timeline",
  5:  "CP5 — Authority — Project / Initiative",
  6:  "CP6 — Sign-off",
  7:  "CP7 — Org Structure",
  8:  "CP8 — Competitive",
  9:  "CP9 — Compliance",
  10: "CP10 — Legal",
};

// Canon CP weights — imported from canonical source (W66.K).
// DO NOT redefine here. Use the import above.
export { CP_WEIGHTS };

// Canon palette
export const GATE_COLORS = {
  green:  "#16A34A",
  yellow: "#EAB308",
  orange: "#EA580C",
  red:    "#DC2626",
  gray:   "#9CA3AF",
} as const;

export type GateStatus = keyof typeof GATE_COLORS;

// ── Types ─────────────────────────────────────────────────────────────────
export interface CpProgress {
  cp_number: number;
  status: GateStatus | string;
  answered: number;
  total: number;
  percent: number;
  answers?: AnswerRecord[];
}

export interface Question {
  question_key: string;
  cp_number: number;
  question_text: string;
  response_type: string;  // "select" | "multi-select" | "text-short" | "text-long" | "currency" | "date"
  options?: Array<{ key: string; label: string; flag?: string; points?: number }> | string[];
  weight?: number;
  is_answered?: boolean;
  current_answer?: AnswerRecord | null;
}

export interface AnswerRecord {
  question_key: string;
  answer_value: string | null;
  answer_notes?: string;
  is_evidence?: boolean;
  evidence_url?: string;
  evidence_text?: string;
  answered_at?: string;
}

export interface AccountHeader {
  id: number;
  name: string;
  composite_score?: number;
  score?: number;
  status?: string;
  stage?: string;
  account_cp_status?: Array<{ cp_number: number; status: string }>;
}

export interface SavePayload {
  answer_value: string | null;
  answer_notes?: string;
  is_evidence?: boolean;
  evidence_url?: string;
  evidence_text?: string;
  evidence_file_url?: string;
}

// ── Hook ─────────────────────────────────────────────────────────────────
export function useAnswerLane(accountId: number) {
  const qc = useQueryClient();

  // Account header
  const accountQ = useQuery<AccountHeader>({
    queryKey: ["/api/accounts", accountId],
    queryFn: () => api(`/api/accounts/${accountId}`),
    enabled: !!accountId,
  });

  // CP progress
  const cpProgressQ = useQuery<CpProgress[]>({
    queryKey: ["/api/accounts", accountId, "cp-progress"],
    queryFn: () => api(`/api/accounts/${accountId}/cp-progress`),
    enabled: !!accountId,
  });

  // Questions with answer state
  const questionsQ = useQuery<Question[]>({
    queryKey: ["/api/accounts", accountId, "questions"],
    queryFn: () => api(`/api/accounts/${accountId}/questions`),
    enabled: !!accountId,
  });

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async ({ questionKey, payload }: { questionKey: string; payload: SavePayload }) => {
      return api(`/api/accounts/${accountId}/answers/${questionKey}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/accounts", accountId, "cp-progress"] });
      qc.invalidateQueries({ queryKey: ["/api/accounts", accountId, "questions"] });
      qc.invalidateQueries({ queryKey: ["/api/accounts", accountId] });
    },
  });

  // Debounced autosave state
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const saveAnswer = useCallback(
    async (questionKey: string, payload: SavePayload, opts?: { immediate?: boolean }) => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);

      const doSave = async () => {
        setSavingKey(questionKey);
        try {
          await saveMutation.mutateAsync({ questionKey, payload });
          setLastSavedAt(new Date());
        } finally {
          setSavingKey(null);
        }
      };

      if (opts?.immediate) {
        await doSave();
      } else {
        debounceTimer.current = setTimeout(doSave, 800);
      }
    },
    [saveMutation],
  );

  // Derived: next unanswered question in a given CP
  const getNextUnansweredInCp = useCallback(
    (cpNumber: number, afterKey?: string): Question | null => {
      const questions = questionsQ.data ?? [];
      const cpQuestions = questions.filter((q) => q.cp_number === cpNumber);
      if (!afterKey) {
        return cpQuestions.find((q) => !q.is_answered) ?? null;
      }
      const idx = cpQuestions.findIndex((q) => q.question_key === afterKey);
      // Try next in same CP
      for (let i = idx + 1; i < cpQuestions.length; i++) {
        if (!cpQuestions[i].is_answered) return cpQuestions[i];
      }
      return null;
    },
    [questionsQ.data],
  );

  const getFirstUnansweredInNextCp = useCallback(
    (cpNumber: number): { question: Question; cpNumber: number } | null => {
      const questions = questionsQ.data ?? [];
      for (let cp = cpNumber + 1; cp <= 10; cp++) {
        const q = questions.find((q) => q.cp_number === cp && !q.is_answered);
        if (q) return { question: q, cpNumber: cp };
      }
      return null;
    },
    [questionsQ.data],
  );

  // Next best question (highest weight unanswered)
  const nextBestQuestion = useCallback((): Question | null => {
    const questions = questionsQ.data ?? [];
    const unanswered = questions.filter((q) => !q.is_answered);
    if (unanswered.length === 0) return null;
    return unanswered.reduce((best, q) => {
      const bw = best.weight ?? 1;
      const qw = q.weight ?? 1;
      return qw > bw ? q : best;
    });
  }, [questionsQ.data]);

  // Totals
  const totalAnswered = (questionsQ.data ?? []).filter((q) => q.is_answered).length;
  const totalQuestions = (questionsQ.data ?? []).length;

  return {
    account: accountQ.data ?? null,
    accountLoading: accountQ.isLoading,
    cpProgress: cpProgressQ.data ?? [],
    cpProgressLoading: cpProgressQ.isLoading,
    questions: questionsQ.data ?? [],
    questionsLoading: questionsQ.isLoading,
    saveAnswer,
    savingKey,
    lastSavedAt,
    getNextUnansweredInCp,
    getFirstUnansweredInNextCp,
    nextBestQuestion,
    totalAnswered,
    totalQuestions,
    isSaveError: saveMutation.isError,
  };
}
